import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useIsAuthenticated, useAccount, useMsal } from '@azure/msal-react';
import { useQuery } from 'react-query';
import { getProfile, getUserStatus } from '../services/user';

type AuthContextData = {
  isLoading: boolean;
  error?: Error;
  isAuthenticated: boolean;
  userProfile: any;
  userName?: string;
  userId?: string;
  isAdmin: boolean;
  isActive: boolean;
};

const defaultContextData = {
  isLoading: true,
  error: undefined,
  isAuthenticated: false,
  userProfile: undefined,
  isAdmin: false,
  isActive: false,
  userId: undefined,
} as AuthContextData;

const AuthContext = createContext<AuthContextData>(defaultContextData);

export const useAuth = () => useContext(AuthContext);

type AuthProviderProps = {
  children: ReactNode | ReactNode[];
};

/**
 * Provides authorization checks for an app
 *
 * Usage: Wrap some component under MsalProvider tree
 * <MsalProvider instance={msalInstance}>
 *     <AuthProvider>
 *       <App />
 *     </AuthProvider>
 *    ...
 * */
export function AuthProvider({ children }: AuthProviderProps) {
  const { accounts } = useMsal(); // Get MSAL accounts
  const isAuthenticated = useIsAuthenticated(); // Check if user is authenticated
  const account = useAccount(accounts[0]); // Get the first account
  const userName = account?.name; // Extract the user name from the account
  const userId = account?.localAccountId; // Extract the user ID from the account

  // Query to fetch the user profile
  const {
    data: userProfile,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery<any, Error>('userProfile', getProfile);

  // Query to fetch the user status
  const {
    data: userStatus,
    isLoading: statusLoading,
    error: statusError,
  } = useQuery<any, Error>('userStatus', getUserStatus);

  // Combine loading states
  const isLoading = profileLoading || statusLoading;
  // Combine errors and ensure the error is either Error or undefined
  const error = profileError ?? statusError ?? undefined;

  // Create context data using useMemo to avoid unnecessary recalculations
  const contextData: AuthContextData = useMemo(
    () => ({
      isAuthenticated,
      userName,
      userId,
      userProfile,
      error,
      isLoading,
      isAdmin: userStatus?.isAdmin || false, // Default to false if userStatus is undefined
      isActive: userStatus?.isActive || false, // Default to false if userStatus is undefined
    }),
    [isAuthenticated, userName, userId, userProfile, userStatus, isLoading, error],
  );

  return <AuthContext.Provider value={contextData}>{children}</AuthContext.Provider>;
}
